import React from "react";
import { AlertCircleIcon, AlertTriangleIcon, ArrowDownIcon, ArrowRightIcon, CheckCircle2Icon, CircleCheckBigIcon, FileCheckIcon, FileTextIcon, MessageCircleIcon, PhoneIcon, ShieldCheckIcon, SparkleIcon, SparklesIcon, UsersIcon, BoxIcon } from "lucide-react";
import { Bi, JourneyStep, StepIcon } from "../../data/trustChallenge";
type Pick = (b: Bi) => string;
type Variant = 'problem' | 'trusted';
const STEP_ICONS: Record<StepIcon, BoxIcon> = {
  question: MessageCircleIcon,
  ai: SparklesIcon,
  wrong: AlertCircleIcon,
  call: PhoneIcon,
  employee: UsersIcon,
  correction: FileTextIcon,
  atna: SparkleIcon,
  verify: ShieldCheckIcon,
  answer: FileCheckIcon,
  action: CircleCheckBigIcon
};
function iconColor(step: JourneyStep, variant: Variant) {
  if (step.tone === 'alert') return 'text-[#E5484D]';
  if (step.tone === 'success' || step.icon === 'verify') return 'text-[#22A06B]';
  if (variant === 'trusted') return 'text-[#4F46E5]';
  return 'text-[#121A3A]';
}
function detailClass(step: JourneyStep) {
  if (step.tone === 'alert') return 'bg-[#FDE8E8] text-[#C9373C]';
  if (step.tone === 'success') return 'bg-[#E6F6EE] text-[#1C7A52]';
  if (step.tone === 'indigo') return 'bg-[#EEEDFD] text-[#4F46E5]';
  return 'bg-[#F4F5F8] text-[#4A5268]';
}
function StepCard({
  step,
  variant,
  pick
}: {
  step: JourneyStep;
  variant: Variant;
  pick: Pick;
}) {
  const Icon = STEP_ICONS[step.icon];
  const alert = step.tone === 'alert';
  return <div className={`flex-1 min-w-0 rounded-xl border px-2 py-3 flex flex-col items-center text-center ${alert ? 'bg-[#FEF3F3] border-[#F6C9C9]' : 'bg-white border-[#ECEDF3]'} shadow-[0_6px_18px_-14px_rgba(18,26,58,0.3)]`} data-id="element-7053" data-mp="91buf0quahm5">
      <Icon className={`w-6 h-6 ${iconColor(step, variant)}`} strokeWidth={1.7} aria-hidden="true" data-id="element-7054" data-mp="3qg0cufh6mh0p" />
      <p className={`mt-2 min-h-[34px] flex items-center font-sans font-semibold leading-tight text-[#121A3A] ${step.icon === 'atna' ? 'text-[15px]' : 'text-[12.5px]'}`} data-id="element-7055" data-mp="rpgrxqgwl6ci">
        {pick(step.title)}
      </p>
      {step.detail && <p className={`mt-2.5 w-full flex-1 flex items-center justify-center rounded-lg px-1.5 py-2 font-sans text-[11px] leading-snug ${detailClass(step)}`} data-id="element-7056" data-mp="2h7x4p4s8asyx">
          {pick(step.detail)}
        </p>}
      {step.checks && <ul className="mt-2.5 w-full space-y-1.5 text-left" data-id="element-7057" data-mp="27g9fzbn23q15">
          {step.checks.map(c => <li key={c.fr} className="flex items-center gap-1.5 font-sans text-[11px] text-[#3A4260] leading-tight" data-id="element-7058" data-mp="2cf3zygwrfkmu">
              <CheckCircle2Icon className="w-3.5 h-3.5 shrink-0 text-white fill-[#22A06B]" aria-hidden="true" data-id="element-7059" data-mp="1xhbmduarx6ba" />
              {pick(c)}
            </li>)}
        </ul>}
    </div>;
}
function MobileStep({
  step,
  variant,
  pick
}: {
  step: JourneyStep;
  variant: Variant;
  pick: Pick;
}) {
  const Icon = STEP_ICONS[step.icon];
  const alert = step.tone === 'alert';
  return <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${alert ? 'bg-[#FEF3F3] border-[#F6C9C9]' : 'bg-white border-[#ECEDF3]'}`} data-id="element-7060" data-mp="3ivy5a9ns9154">
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconColor(step, variant)}`} strokeWidth={1.7} aria-hidden="true" data-id="element-7061" data-mp="3nt83adp8ml4k" />
      <div className="min-w-0 flex-1" data-id="element-7062" data-mp="3t04tzc4he51n">
        <p className="font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-7063" data-mp="3t17k3htzc3gi">{pick(step.title)}</p>
        {step.detail && <p className={`mt-1.5 inline-block rounded-md px-2 py-1 font-sans text-[12.5px] ${detailClass(step)}`} data-id="element-7064" data-mp="amylfpilmt23">{pick(step.detail)}</p>}
        {step.checks && <ul className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-7065" data-mp="kkcpepnc5pdi">
            {step.checks.map(c => <li key={c.fr} className="flex items-center gap-1.5 font-sans text-[12px] text-[#3A4260]" data-id="element-7066" data-mp="km5z6dt8wppq">
                <CheckCircle2Icon className="w-3.5 h-3.5 shrink-0 text-white fill-[#22A06B]" aria-hidden="true" data-id="element-7067" data-mp="fnm8y17mcrrk" />
                {pick(c)}
              </li>)}
          </ul>}
      </div>
    </div>;
}
type JourneyProps = {
  variant: Variant;
  title: string;
  sub: string;
  steps: JourneyStep[];
  result: string;
  pick: Pick;
};
export function TrustJourneyPanel({
  variant,
  title,
  sub,
  steps,
  result,
  pick
}: JourneyProps) {
  const problem = variant === 'problem';
  const line = problem ? '#EF9A9C' : '#8B84F0';
  const arrow = problem ? 'text-[#D9585C]' : 'text-[#4F46E5]';
  return <div className={`rounded-2xl border p-5 md:p-7 ${problem ? 'bg-[#FFF8F7] border-[#F7E3E1]' : 'bg-[#F7F7FE] border-[#E6E5F8]'}`} data-id="element-7068" data-mp="2xsvuml6hmjz2">
      <div className="flex items-center gap-4" data-id="element-7069" data-mp="36s943dum38js">
        <span className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${problem ? 'bg-[#FDE3E3]' : 'bg-[#E9E8FC]'}`} data-id="element-7070" data-mp="36qy1hhcwk6u9">
          {problem ? <AlertTriangleIcon className="w-6 h-6 text-[#E5484D]" strokeWidth={1.7} aria-hidden="true" data-id="element-7071" data-mp="2e2k3v2pq7cj8" /> : <SparklesIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-7072" data-mp="2j1myrubzs2dh" />}
        </span>
        <div data-id="element-7073" data-mp="2nt4x0ammrour">
          <h3 className="font-sans text-[1.35rem] md:text-[1.55rem] font-semibold tracking-tight text-[#121A3A]" data-id="element-7074" data-mp="2ntn5c5mkhtj1">{title}</h3>
          <p className="mt-0.5 font-sans text-[14.5px] text-[#5A6178]" data-id="element-7075" data-mp="1qd7n9hs2k26w">{sub}</p>
        </div>
      </div>

      {/* Horizontal journey — tablet & desktop */}
      <ol className="hidden md:flex mt-6 items-stretch gap-1.5" data-id="element-7076" data-mp="1ntba2zwbqmx3">
        {steps.map((s, i) => <React.Fragment key={s.title.fr} data-id="element-7077" data-mp="1itgjyykdi1d3" data-mpi={i}>
            <li className={`flex ${s.checks ? 'flex-[1.35]' : 'flex-1'} min-w-0`} data-id="element-7078" data-mp="1iurl0qmfov0i">
              <StepCard step={s} variant={variant} pick={pick} data-id="element-7079" data-mp="1dtkawkwtjjqo" />
            </li>
            {i < steps.length - 1 && <li className="flex items-center" aria-hidden="true" data-id="element-7080" data-mp="19sqoxx2xsf0f">
                <ArrowRightIcon className={`w-3.5 h-3.5 ${arrow}`} strokeWidth={2} data-id="element-7081" data-mp="14ugszk9e1adr" />
              </li>}
          </React.Fragment>)}
      </ol>
      <svg className="hidden md:block mt-2 w-full h-5" viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true" data-id="element-7082" data-mp="1025t2hendol7">
        {problem ? <path d="M2 2 Q 2 10 6 10 L 94 10 Q 98 10 98 2" fill="none" stroke={line} strokeWidth="1.2" vectorEffect="non-scaling-stroke" data-id="element-7083" data-mp="q5atdv6n1i5c" /> : <path d="M2 2 Q 2 10 6 10 L 46 10 Q 50 10 50 18 Q 50 10 54 10 L 94 10 Q 98 10 98 2" fill="none" stroke={line} strokeWidth="1.2" vectorEffect="non-scaling-stroke" data-id="element-7084" data-mp="m3z6vyj29d1d" />}
      </svg>

      {/* Vertical journey — mobile */}
      <ol className="md:hidden mt-5 flex flex-col" data-id="element-7085" data-mp="7fflaf1sx1pb">
        {steps.map((s, i) => <React.Fragment key={s.title.fr} data-id="element-7086" data-mp="bjnaseg32lol" data-mpi={i}>
            <li data-id="element-7087" data-mp="bghloz7gfjsv">
              <MobileStep step={s} variant={variant} pick={pick} data-id="element-7088" data-mp="3to72mvrcgxhb" />
            </li>
            {i < steps.length - 1 && <li className="flex justify-center py-1" aria-hidden="true" data-id="element-7089" data-mp="2hnr1edop3hy">
                <ArrowDownIcon className={`w-4 h-4 ${arrow}`} strokeWidth={2} data-id="element-7090" data-mp="2jqt4ws42n31" />
              </li>}
          </React.Fragment>)}
      </ol>

      <div className="mt-4 md:mt-2 flex justify-center" data-id="element-7091" data-mp="v732u6d6b0ex">
        <p className={`inline-flex items-center gap-3 rounded-full border bg-white px-5 py-2.5 font-sans text-[14px] md:text-[15px] font-medium ${problem ? 'border-[#F4CACA] text-[#D2383D]' : 'border-[#D9D6FA] text-[#4F46E5]'}`} data-id="element-7092" data-mp="v8nwltrnyfbq">
          {problem ? <AlertCircleIcon className="w-5 h-5 shrink-0 text-white fill-[#E5484D]" aria-hidden="true" data-id="element-7093" data-mp="195uhpuzv0q44" /> : <CheckCircle2Icon className="w-5 h-5 shrink-0 text-white fill-[#4F46E5]" aria-hidden="true" data-id="element-7094" data-mp="1dxkv69u3ftzr" />}
          {result}
        </p>
      </div>
    </div>;
}